import React, { useState } from 'react';
import { Flag, Calendar, Edit, Trash2, Plus, TrendingUp } from 'lucide-react';
import { Button, Badge, Modal, ConfirmModal, Card, CardContent } from '@/components/ui';
import type { Milestone, MilestoneType } from '@/types';
import MilestoneForm from './MilestoneForm';

interface MilestoneListProps {
  milestones: Milestone[];
  epicId: string;
  isLoading?: boolean;
  onCreateMilestone: (data: Partial<Milestone>) => Promise<void>;
  onUpdateMilestone: (id: string, data: Partial<Milestone>) => Promise<void>;
  onDeleteMilestone: (id: string) => Promise<void>;
}

const MilestoneList: React.FC<MilestoneListProps> = ({
  milestones,
  epicId,
  isLoading = false,
  onCreateMilestone,
  onUpdateMilestone,
  onDeleteMilestone,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async (data: Partial<Milestone>) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onCreateMilestone({ ...data, epicId });
      setIsCreateModalOpen(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to create milestone');
      console.error('Error creating milestone:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: Partial<Milestone>) => {
    if (!selectedMilestone) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onUpdateMilestone(selectedMilestone.id, data);
      setIsEditModalOpen(false);
      setSelectedMilestone(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to update milestone');
      console.error('Error updating milestone:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMilestone) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onDeleteMilestone(selectedMilestone.id);
      setIsDeleteModalOpen(false);
      setSelectedMilestone(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete milestone');
      console.error('Error deleting milestone:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMilestoneTypeColor = (type: MilestoneType) => {
    return type === 'BUSINESS' ? 'text-accent-blue' : 'text-accent-violet';
  };

  const getMilestoneTypeBadge = (type: MilestoneType) => {
    return type === 'BUSINESS' ? 'primary' : 'secondary';
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage === 100) return 'text-status-success';
    if (percentage >= 75) return 'text-accent-blue';
    if (percentage >= 50) return 'text-status-warning';
    return 'text-status-error';
  };

  const getCompletionBgColor = (percentage: number) => {
    if (percentage === 100) return 'bg-status-success';
    if (percentage >= 75) return 'bg-accent-blue';
    if (percentage >= 50) return 'bg-status-warning';
    return 'bg-status-error';
  };

  const getDaysUntilTarget = (targetDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return { text: `${Math.abs(daysUntil)} days overdue`, color: 'text-status-error', icon: '⚠️' };
    } else if (daysUntil === 0) {
      return { text: 'Due today', color: 'text-status-warning', icon: '🎯' };
    } else if (daysUntil <= 7) {
      return { text: `${daysUntil} days left`, color: 'text-status-warning', icon: '⏰' };
    } else if (daysUntil <= 30) {
      return { text: `${daysUntil} days left`, color: 'text-accent-blue', icon: '' };
    } else {
      const months = Math.floor(daysUntil / 30);
      return { text: `${months} month${months > 1 ? 's' : ''} left`, color: 'text-[var(--muted)]', icon: '📅' };
    }
  };

  const sortedMilestones = [...milestones].sort((a, b) => {
    // Sort by target date, earliest first
    return new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime();
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--muted)]">Loading milestones...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-[var(--text)]">Milestones</h3>
          <p className="text-sm text-[var(--muted)] mt-1">
            Track critical milestones and completion progress for this EPIC
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add Milestone
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-status-error" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-status-error">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 text-status-error hover:text-status-error/80"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Milestones List */}
      {sortedMilestones.length > 0 ? (
        <div className="space-y-3">
          {sortedMilestones.map((milestone) => {
            const daysUntil = getDaysUntilTarget(milestone.targetDate);

            return (
              <Card key={milestone.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Milestone Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Flag className={`w-4 h-4 ${getMilestoneTypeColor(milestone.type)} flex-shrink-0`} />
                        <Badge variant={getMilestoneTypeBadge(milestone.type)} className="text-xs">
                          {milestone.type}
                        </Badge>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-[var(--muted)]" />
                          <span className="text-[var(--text)]">
                            {new Date(milestone.targetDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        <span className={`text-xs ${daysUntil.color} flex items-center gap-1`}>
                          <span>{daysUntil.icon}</span>
                          {daysUntil.text}
                        </span>
                      </div>

                      {milestone.description && (
                        <p className="text-sm text-[var(--muted)] leading-relaxed line-clamp-2 mb-3">
                          {milestone.description}
                        </p>
                      )}

                      {/* Completion Progress */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[var(--muted)]">Completion Progress</span>
                          <span className={`font-semibold ${getCompletionColor(milestone.completionPercentage)}`}>
                            {milestone.completionPercentage}%
                          </span>
                        </div>
                        <div className="w-full bg-[var(--surface-muted)] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${getCompletionBgColor(milestone.completionPercentage)}`}
                            style={{ width: `${milestone.completionPercentage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedMilestone(milestone);
                          setIsEditModalOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedMilestone(milestone);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-status-error" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-[var(--surface-muted)] rounded-lg border-2 border-dashed border-[var(--border)]">
          <Flag className="w-12 h-12 text-[var(--muted)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text)] mb-2">No Milestones Yet</h3>
          <p className="text-[var(--muted)] mb-4">
            Track critical milestones to monitor progress toward key deliverables
          </p>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add First Milestone
          </Button>
        </div>
      )}

      {/* Create Milestone Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Milestone"
        size="lg"
      >
        <MilestoneForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Edit Milestone Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedMilestone(null);
        }}
        title="Edit Milestone"
        size="lg"
      >
        {selectedMilestone && (
          <MilestoneForm
            initialData={selectedMilestone}
            onSubmit={handleUpdate}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedMilestone(null);
            }}
            isLoading={isSubmitting}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedMilestone(null);
        }}
        onConfirm={handleDelete}
        title="Delete Milestone"
        message={`Are you sure you want to delete this milestone? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default MilestoneList;
